import {cloneDeep} from "lodash"
import {DataProxy} from "apollo-cache"
import sentry from "@irati/utils/services/sentry"
import {RecognitionFeedList, RecognitionFeed, Poll, Recognition} from "@irati/types"
import {feedPollQuery, FeedPollVariables} from "../query/FeedPoll"
import {feedRecognitionQuery, FeedRecognitionVariables} from "../query/FeedRecognition"
import {FeedItemType, FeedVisibility} from "../../enums"

type FeedItem = Poll | Recognition

export interface FeedEntity extends Omit<RecognitionFeed, "recognition" | "poll"> {
  recognition: Recognition | null,
  poll: Poll | null
}

interface FeedUpdater {
  (feed: string): void
}

interface Feeds {
  [name: string]: boolean
}

export const updateFeeds = (item: FeedItem) => {
  const isEverything = true
  const isMyTeam = item.private || item.teams.results.length > 0
  const isPrivate = item.private

  const feeds: Feeds = Object.freeze({
    [FeedVisibility.EVERYTHING]: isEverything,
    [FeedVisibility.MY_FEED]: isMyTeam,
    [FeedVisibility.JUST_ME]: isPrivate
  })

  return (updater: FeedUpdater) => {
    Object.keys(feeds).forEach((feed: string) => {
      const shouldBeUpdated = feeds[feed]
      if (shouldBeUpdated) {
        try {
          updater(feed)
        } catch (e) {
          sentry.captureException(e)
        }
      }
    })
  }
}

export const addItemToFeed = (feed: RecognitionFeedList, newItem: RecognitionFeed): RecognitionFeedList => {
  if (!newItem) {
    return feed
  }

  const clonedItem = cloneDeep(newItem)
  const clonedFeed = cloneDeep(feed)

  clonedFeed.results.unshift(clonedItem)

  return clonedFeed
}

type FeedItemQuery = DataProxy.Query<FeedPollVariables | FeedRecognitionVariables>

export const getFeedItemQueryByType = (type: string, parentId: string): FeedItemQuery | null => {
  if (type !== FeedItemType.POLL && type !== FeedItemType.RECOGNITION) {
    return null
  }

  const query = type === FeedItemType.POLL ? feedPollQuery : feedRecognitionQuery

  return {
    ...query,
    variables: {
      id: parentId
    }
  }
}

export const getItemType = (item: FeedItem): FeedItemType => ("receivers" in item ? FeedItemType.RECOGNITION : FeedItemType.POLL)

const RECOGNITION_FEED_ITEM_TYPE = "RecognitionFeed"

export const getFeedEntityFromRecognition = (recognition: Recognition): FeedEntity => ({
  __typename: RECOGNITION_FEED_ITEM_TYPE,
  uuid: recognition.uuid,
  type: FeedItemType.RECOGNITION,
  recognition,
  poll: null
})

export const getFeedItemFromPoll = (poll: Poll): FeedEntity => ({
  __typename: RECOGNITION_FEED_ITEM_TYPE,
  uuid: poll.uuid,
  type: FeedItemType.POLL,
  recognition: null,
  poll
})
